<template>
  <div
    v-if="useClassicGrid && !draft && inAppCheck && showComponentInVMXOnly"
    class="component-grid"
    :class="containerClass"
    :style="styles"
  >
    <div
      v-if="componentIsActive"
      :style="
        backgroundImage ? `background-image: url('${backgroundImage}')` : ''
      "
      :class="backgroundImage ? 'component-grid__background-container' : ''"
    >
      <div
        v-if="componentIsActive"
        class="component-grid__container"
        :class="{ 'vm-container': vmContainer }"
      >
        <div class="component-grid__items">
          <slot />
        </div>
      </div>
    </div>
  </div>
  <div
    v-else-if="grid.newGridActive && !useClassicGrid && !draft && inAppCheck && showComponentInVMXOnly"
    class="component-grid"
    :class="containerClass"
    :style="styles"
  >
    <div
      v-if="componentIsActive"
      :style="
        backgroundImage ? `background-image: url('${backgroundImage}')` : ''
      "
      :class="backgroundImage ? 'component-grid__background-container' : ''"
    >
      <div
        v-if="componentIsActive"
        class="component-grid__container"
        :class="{ 'vm-container': vmContainer }"
      >
        <div
          class="component-grid__items component-grid__items-grid"
          :style="gridStyles"
        >
          <slot />
        </div>
      </div>
    </div>
  </div>
  <div v-else-if="!draft && inAppCheck && showComponentInVMXOnly" class="component-grid">
    <div
      :class="{
        'component-grid__container--full-width': fullWidth,
        'component-grid__container': compGridContainerActive,
      }"
    >
      <div :class="{ 'component-grid__items': compGridItemsActive }">
        <slot />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import { replaceCssVar } from "../../utilities/theme";
import componentIsActiveMixins from "../../mixins/componentIsActiveMixins";

export default {
  name: "ComponentGrid",
  mixins: [
    componentIsActiveMixins.componentProps,
    componentIsActiveMixins.method,
  ],
  props: {
    draft: {
      type: Boolean,
      default: false,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    backgroundColor: {
      type: String,
      default: null,
    },
    textColor: {
      type: String,
      default: null,
    },
    backgroundImage: {
      type: String,
      default: null,
    },
    classicGridActive: {
      type: Boolean,
      default: true,
    },
    newGridActive: {
      type: Boolean,
      default: false,
    },
    grid: {
      type: Object,
      default() {
        return {};
      },
    },
    vmContainer: {
      type: Boolean,
      default: false,
    },
    onlyInVMX: {
      type: Boolean,
      default: false,
    },
    inApp: {
      type: Boolean,
      default: true,
    },
    onlyInApp: {
      type: Boolean,
      default: false,
    },
    compGridContainerActive: {
      type: Boolean,
      default: false,
    },
    compGridItemsActive: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    windowWidth: window.innerWidth,
  }),
  computed: {
    ...mapGetters({
      inVMX: "instancesState/getInVMX",
      isAppContainer: "instancesState/getIsAppContainer",
    }),
    inAppCheck() {
      if (this.isAppContainer) {
        return this.inApp;
      }
      return !this.onlyInApp;
    },
    showComponentInVMXOnly() {
      if (this.onlyInVMX && this.inVMX) return true;
      if (this.onlyInVMX && !this.inVMX) return false;
      return true;
    },
    styles() {
      const styles = {};
      if (this.textColor) {
        styles.color = this.replCssVar(this.textColor);
      }
      if (this.backgroundColor) {
        styles.backgroundColor = this.replCssVar(this.backgroundColor);
      }
      return styles;
    },
    gridStyles() {
      const styles = {};
      styles.display = "grid";
      if (this.grid.gridGap) {
        styles.gap = this.grid.gridGap;
      }
      if (
        !this.grid.defaultGrid &&
        this.windowWidth >= (this.grid.windowWidthDesktop || 992)
      ) {
        styles.gridTemplateColumns = this.grid.gridTemplateColumns;
      }
      if (
        !this.grid.defaultGrid &&
        this.windowWidth >= (this.grid.windowWidthTablet || 768) &&
        this.windowWidth <= (this.grid.windowWidthDesktop - 1 || 991)
      ) {
        styles.gridTemplateColumns = this.grid.gridTemplateColumnsTablet;
      }
      if (
        !this.grid.defaultGrid &&
        this.windowWidth <= (this.grid.windowWidthPhone || 767)
      ) {
        styles.gridTemplateColumns = this.grid.gridTemplateColumnsPhone;
      }
      if (this.grid.gridTemplateColumns && this.grid.defaultGrid) {
        styles.gridTemplateColumns = "repeat(auto-fit, minmax(320px, 1fr))";
      }
      if (
        this.grid.gridTemplateRows &&
        this.windowWidth >= (this.grid.windowWidthDesktop || 992)
      ) {
        styles.gridTemplateRows = this.grid.gridTemplateRows + "!important";
      }
      if (
        this.grid.gridTemplateRowsTablet &&
        this.windowWidth >= (this.grid.windowWidthTablet || 768) &&
        this.windowWidth <= (this.grid.windowWidthDesktop - 1 || 991)
      ) {
        styles.gridTemplateRows =
          this.grid.gridTemplateRowsTablet + "!important";
      }
      if (
        this.grid.gridTemplateRowsPhone &&
        this.windowWidth <= (this.grid.windowWidthPhone || 767)
      ) {
        styles.gridTemplateRows =
          this.grid.gridTemplateRowsPhone + "!important";
      }
      if (this.grid.gridAutoRows) {
        styles.gridAutoRows = this.grid.gridAutoRows;
      }
      if (
        this.grid.useTemplateAreas &&
        this.windowWidth >= (this.grid.windowWidthDesktop || 992)
      ) {
        styles.gridTemplateAreas = this.grid.gridTemplateAreas + "!important";
      }
      if (
        this.grid.useTemplateAreas &&
        this.windowWidth >= (this.grid.windowWidthTablet || 768) &&
        this.windowWidth <= (this.grid.windowWidthDesktop - 1 || 991)
      ) {
        styles.gridTemplateAreas =
          this.grid.gridTemplateAreasTablet + "!important";
      }
      if (
        this.grid.useTemplateAreas &&
        this.windowWidth <= (this.grid.windowWidthPhone || 767)
      ) {
        styles.gridTemplateAreas =
          this.grid.gridTemplateAreasPhone + "!important";
      }
      return styles;
    },
    containerClass() {
      const classes = [];
      if (this.fullWidth) {
        classes.push("component-grid__container--full-width");
      }
      return classes;
    },
    useClassicGrid() {
      if (this.grid.newGridActive === true) {
        return false;
      } else return this.classicGridActive;
    },
  },
  created() {
    window.addEventListener("resize", this.onResize);
  },
  methods: {
    replCssVar(val) {
      return replaceCssVar(val);
    },
    onResize() {
      this.windowWidth = window.innerWidth;
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->

<style lang="scss">
.component-grid--bg-grey {
  background: #e1e1e1;
}

.component-grid__container.vm-container {
  width: 100%;
  @media (min-width: 992px) {
    padding-left: 2rem !important;
    padding-right: 2rem !important;
  }
  @media (max-width: 991px) {
    padding-left: 1.1rem !important;
    padding-right: 1.1rem !important;
  }
  margin-right: auto;
  margin-left: auto;
  box-sizing: border-box;
  max-width: 1400px !important;
}

.component-grid__container {
  width: 100%;
  padding-left: 0 !important;
  padding-right: 0 !important;
  margin-right: auto;
  margin-left: auto;
  box-sizing: border-box;
  overflow: visible;
}

.component-grid__background-container {
  padding-top: calc(2 * var(--link-box-grid-gap));
  padding-bottom: calc(2 * var(--link-box-grid-gap));
  background-size: cover;
  background-position: bottom right;
}

.component-grid__container--full-width {
  .component-grid__container {
    max-width: 100%;
    padding-left: 0 !important;
    padding-right: 0 !important;
    width: 100% !important;
  }
}

@media (min-width: 992px) {
  .component-grid__container {
    width: 100%;
  }
}

.component-grid__items {
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  margin-bottom: 0.5rem;
  /* margin-right: -$c-grid-gutter; */
  /* padding-top: $c-grid-gutter; */
  overflow: visible;
  gap: var(--link-box-grid-gap);
}
</style>
